import { FC, useEffect, useState } from 'react'
import {
	Alert,
	Button,
	Container,
	Header,
	SpaceBetween
} from '@cloudscape-design/components'

import api from '../../../../api'
import ReadGenericTagsGCP from '../../../../components/ControlledFormInputs/GenericTagsReadGCP'
import GcpPlatformTagsNew from '../../../../pages/platforms/new/GcpPlatformNewTags'
import { useNewPlatformFormStore } from '../../../../stores/platforms/newPlatformFormStore'
import { useGenericTagsManagementFormStore } from '../../../../stores/tags/genericTagsManagementFormStore'
import { gcpProject } from '../../../../types/gcpProject'

type GcpResourceTagsProps = {
	registerValidation?: (validate: () => Promise<boolean>) => void
}

type SelectedGcpProject = {
	label: string
	value: string
}

const GcpResourceTags: FC<GcpResourceTagsProps> = ({ registerValidation }) => {
	const [isUsingInheritedTags, setIsUsingInheritedTags] = useState(true)
	const [inheritedTagsId, setInheritedTagsId] = useState('')
	const [inheritanceError, setInheritanceError] = useState('')

	const {
		formValues,
		actions: { setFormValues }
	} = useNewPlatformFormStore()

	const {
		formValues: tagFormValues,
		actions: {
			clearFormValues: clearTagFormValues,
			setFormValues: setTagFormValues
		}
	} = useGenericTagsManagementFormStore()

	const selectedGcpProject = formValues.gcpProjectID as
		| SelectedGcpProject
		| undefined
	const selectedGcpProjectValue = selectedGcpProject?.value
	const selectedGcpProjectLabel = selectedGcpProject?.label

	useEffect(() => {
		if (!isUsingInheritedTags) {
			return
		}

		registerValidation?.(async () =>
			Boolean(selectedGcpProjectValue && inheritedTagsId && !inheritanceError)
		)
	}, [
		inheritedTagsId,
		inheritanceError,
		isUsingInheritedTags,
		registerValidation,
		selectedGcpProjectValue
	])

	useEffect(() => {
		if (isUsingInheritedTags) {
			return
		}

		if (tagFormValues.WBSCode !== '') {
			setFormValues('wbsCode', tagFormValues.WBSCode)
		}

		if (tagFormValues.costCenter !== '') {
			setFormValues('costCenter', tagFormValues.costCenter)
		}

		if (tagFormValues.group !== null && tagFormValues.group !== undefined) {
			setFormValues('group', tagFormValues.group)
		}
	}, [
		isUsingInheritedTags,
		setFormValues,
		tagFormValues.WBSCode,
		tagFormValues.costCenter,
		tagFormValues.group
	])

	useEffect(() => {
		if (isUsingInheritedTags) {
			return
		}

		if (formValues.platformOwner !== '') {
			setTagFormValues('platformOwner', formValues.platformOwner)
		}

		if (formValues.platformName !== '') {
			setTagFormValues('platformName', formValues.platformName)
		}

		if (formValues.systemAlias !== '') {
			setTagFormValues('systemAlias', formValues.systemAlias)
		}
	}, [
		formValues.platformName,
		formValues.platformOwner,
		formValues.systemAlias,
		isUsingInheritedTags,
		setTagFormValues
	])

	useEffect(() => {
		let isMounted = true

		const getProjectWithTags = async (gcpProjectID: SelectedGcpProject) => {
			try {
				const response = await api.gcpProjects.getSinglegcpProject(
					gcpProjectID.value,
					true
				)
				const project = response.body[0] as gcpProject
				const tagDetails = project.tags?.details
				const tagsId = project.tagsID || project.tags?.tagsID

				if (!isMounted) {
					return
				}

				if (!tagsId || !tagDetails) {
					clearTagFormValues()
					setInheritedTagsId('')
					setInheritanceError('Selected GCP Project does not have approved resource tags to inherit.')
					setFormValues('tagsID', '')
					setFormValues('wbsCode', '')
					setFormValues('costCenter', '')
					setFormValues('group', '')
					return
				}

				setInheritanceError('')
				setInheritedTagsId(tagsId)
				setTagFormValues('platformName', tagDetails.platformName)
				setTagFormValues('group', tagDetails.group)
				setTagFormValues('fundingSourceType', tagDetails.fundingSourceType)
				setTagFormValues('WBSCode', tagDetails.WBSCode)
				setTagFormValues('costCenter', tagDetails.costCenter)
				setTagFormValues('dataClassification', tagDetails.dataClassification)
				setTagFormValues('projectName', gcpProjectID.label)

				setFormValues('inheritGcpTagsBool', true)
				setFormValues('tagsID', tagsId)
				setFormValues('wbsCode', tagDetails.WBSCode || '')
				setFormValues('costCenter', tagDetails.costCenter || '')
				setFormValues('group', tagDetails.group || '')
			} catch (_error) {
				if (!isMounted) {
					return
				}

				clearTagFormValues()
				setInheritedTagsId('')
				setInheritanceError('Unable to load resource tags from the selected GCP Project.')
				setFormValues('tagsID', '')
				setFormValues('wbsCode', '')
				setFormValues('costCenter', '')
				setFormValues('group', '')
			}
		}

		if (!isUsingInheritedTags) {
			return () => {
				isMounted = false
			}
		}

		if (!selectedGcpProjectValue || !selectedGcpProjectLabel) {
			clearTagFormValues()
			setInheritedTagsId('')
			setInheritanceError('')
			setFormValues('inheritGcpTagsBool', false)
			setFormValues('tagsID', '')
			setFormValues('wbsCode', '')
			setFormValues('costCenter', '')
			setFormValues('group', '')
		} else {
			clearTagFormValues()
			setInheritedTagsId('')
			setInheritanceError('')
			setFormValues('tagsID', '')
			setFormValues('wbsCode', '')
			setFormValues('costCenter', '')
			setFormValues('group', '')
			void getProjectWithTags({
				label: selectedGcpProjectLabel,
				value: selectedGcpProjectValue
			})
		}

		return () => {
			isMounted = false
		}
	}, [
		clearTagFormValues,
		isUsingInheritedTags,
		selectedGcpProjectLabel,
		selectedGcpProjectValue,
		setFormValues,
		setTagFormValues
	])

	const clearPlatformTagFields = () => {
		setFormValues('tagsID', '')
		setFormValues('wbsCode', '')
		setFormValues('costCenter', '')
		setFormValues('group', '')
	}

	const switchToNewTags = () => {
		setIsUsingInheritedTags(false)
		clearTagFormValues()
		setInheritedTagsId('')
		setInheritanceError('')
		setFormValues('inheritGcpTagsBool', false)
		clearPlatformTagFields()
	}

	const switchToInheritedTags = () => {
		setIsUsingInheritedTags(true)
		clearTagFormValues()
		setInheritedTagsId('')
		setInheritanceError('')
		setFormValues('inheritGcpTagsBool', true)
		clearPlatformTagFields()
	}

	return (
		<Container
			header={
				<Header
					variant="h2"
					actions={
						selectedGcpProjectValue && (
							<SpaceBetween direction="horizontal" size="xs">
								{isUsingInheritedTags ? (
									<Button variant="link" onClick={switchToNewTags}>
										+ Create New Tags
									</Button>
								) : (
									<Button variant="link" onClick={switchToInheritedTags}>
										- Switch to GCP Project Tags
									</Button>
								)}
							</SpaceBetween>
						)
					}
				>
					Resource Tags
				</Header>
			}
		>
			<SpaceBetween direction="vertical" size="l">
				{isUsingInheritedTags ? (
					<>
						{!selectedGcpProjectValue && (
							<Alert type="warning">
								Select a GCP Project in Cluster Details to inherit resource tags.
							</Alert>
						)}
						{selectedGcpProjectValue && !inheritanceError && !inheritedTagsId && (
							<Alert type="info">Loading inherited GCP Project resource tags.</Alert>
						)}
						{inheritanceError && (
							<Alert type="error" header="Resource tags unavailable">
								{inheritanceError}
							</Alert>
						)}
						{inheritedTagsId && <ReadGenericTagsGCP tagsId={inheritedTagsId} />}
					</>
				) : (
					<GcpPlatformTagsNew
						registerValidation={registerValidation}
						isInheritTagsChecked={false}
					/>
				)}
			</SpaceBetween>
		</Container>
	)
}

export default GcpResourceTags
